import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { gql, type ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { mutationErrorMessage } from '../../lib/gqlError';
import { Icon } from '../Icon';
import { LabelChip, splitLabel, type LabelLite } from './LabelChip';

/** Non-archived labels for the picker (names/colours; any signed-in caller may read). */
export const PICKER_LABELS = gql`
  query PickerLabels {
    labels {
      id
      name
      scope
      color
      description
      system
    }
  }
`;

type PickerLabel = { id: number; name: string; scope: string | null; color: string; description: string | null; system: boolean };

export const toLabelLite = (l: { id: number | string; name: string; scope: string | null; color: string; system?: boolean }): LabelLite => ({
  id: String(l.id),
  name: l.name,
  scope: l.scope,
  color: l.color,
  system: l.system,
});

/** Words match anywhere in the name with `:` `-` `_` treated as spaces, so `secret rotation`
 *  finds `type::secret-rotation` (same behaviour as list search). */
export function labelMatches(name: string, q: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[:\-_/]+/g, ' ');
  const hay = norm(name);
  return norm(q).split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

/**
 * Labels widget: the applied chips (removable) plus an "Add label" button that opens a
 * searchable combobox/listbox popover. Choosing an applied label removes it; choosing a scoped
 * label says which label of the same scope it will replace (one per scope — core swaps them).
 */
export function LabelPicker(props: {
  value: LabelLite[];
  onApply: (label: LabelLite) => void | Promise<void>;
  onRemove: (label: LabelLite) => void | Promise<void>;
  disabled?: boolean;
  excludeScopes?: string[];
}) {
  const { value, onApply, onRemove, disabled, excludeScopes } = props;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optId = (i: number) => `${listId}-opt-${i}`;

  const { data, loading, error } = useQuery<{ labels: PickerLabel[] }>(PICKER_LABELS, {
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  const excluded = useMemo(() => new Set((excludeScopes ?? []).map((s) => s.toLowerCase())), [excludeScopes]);
  const appliedIds = useMemo(() => new Set(value.map((l) => l.id)), [value]);
  const byScope = useMemo(() => {
    const m = new Map<string, LabelLite>();
    for (const l of value) {
      const s = l.scope ?? splitLabel(l.name).scope;
      if (s) m.set(s.toLowerCase(), l);
    }
    return m;
  }, [value]);

  const options = useMemo(() => {
    const all = (data?.labels ?? []).filter((l) => !(l.scope && excluded.has(l.scope.toLowerCase())));
    return all
      .filter((l) => !q.trim() || labelMatches(l.name, q))
      .sort((a, b) => Number(appliedIds.has(String(b.id))) - Number(appliedIds.has(String(a.id))) || a.name.localeCompare(b.name));
  }, [data, excluded, q, appliedIds]);

  useEffect(() => setActive(0), [q, open]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  useEffect(() => {
    document.getElementById(optId(active))?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const close = () => {
    setOpen(false);
    setQ('');
    setErr(null);
    triggerRef.current?.focus();
  };

  const run = async (label: LabelLite, fn: (l: LabelLite) => void | Promise<void>) => {
    setBusy(label.id);
    setErr(null);
    try {
      await fn(label);
    } catch (e) {
      setErr(mutationErrorMessage(e as ErrorLike) ?? (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(null);
    }
  };

  const choose = (l: PickerLabel) => {
    if (busy) return;
    const lite = toLabelLite(l);
    void run(lite, appliedIds.has(lite.id) ? onRemove : onApply);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(Math.max(options.length - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const l = options[active];
      if (l) choose(l);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const hintFor = (l: PickerLabel): { text: string; swap: boolean } | null => {
    const applied = appliedIds.has(String(l.id));
    if (applied) return { text: 'Applied — select to remove', swap: false };
    if (!l.scope) return l.description ? { text: l.description, swap: false } : null;
    const holder = byScope.get(l.scope.toLowerCase());
    if (holder) return { text: `Replaces ${holder.name}`, swap: true };
    return { text: l.description || `Only one ${l.scope}:: label per task`, swap: false };
  };

  return (
    <div className="label-picker" ref={rootRef}>
      {/* No "None" placeholder: an empty editable picker is just its "+ Add label" button.
          A read-only one with nothing applied still says so, or the field would be blank. */}
      {value.length ? (
        <div className="label-picker__chips">
          {value.map((l) => (
            <LabelChip
              key={l.id}
              label={l}
              size="sm"
              onRemove={disabled || busy ? undefined : () => void run(l, onRemove)}
            />
          ))}
        </div>
      ) : disabled ? (
        <span className="label-picker__none">No labels</span>
      ) : null}
      {!disabled ? (
        <button
          ref={triggerRef}
          type="button"
          className="btn btn-ghost btn-sm label-picker__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
        >
          {value.length ? (
            <>
              <Icon name="edit" size={14} /> Edit labels
            </>
          ) : (
            '+ Add label'
          )}
        </button>
      ) : null}
      {err && !open ? (
        <div className="label-picker__error" role="alert">
          {err}
        </div>
      ) : null}
      {open ? (
        <div className="label-picker__pop">
          <input
            ref={inputRef}
            className="label-picker__search"
            type="text"
            role="combobox"
            aria-label="Search labels"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={options[active] ? optId(active) : undefined}
            placeholder="Search labels"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
          />
          <ul className="label-picker__list" id={listId} role="listbox" aria-label="Labels" aria-multiselectable="true">
            {options.map((l, i) => {
              const applied = appliedIds.has(String(l.id));
              const hint = hintFor(l);
              return (
                <li
                  key={l.id}
                  id={optId(i)}
                  role="option"
                  className={`label-picker__opt${i === active ? ' is-active' : ''}`}
                  aria-selected={applied}
                  aria-disabled={busy === String(l.id) || undefined}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(l)}
                >
                  <span className="label-picker__check" aria-hidden="true">
                    {applied ? <Icon name="check" size={14} /> : null}
                  </span>
                  <span className="label-picker__opt-main">
                    <span>
                      <LabelChip label={toLabelLite(l)} size="sm" />
                    </span>
                    {hint ? (
                      <span className={`label-picker__hint${hint.swap ? ' label-picker__hint--swap' : ''}`}>{hint.text}</span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {loading && !data ? <div className="label-picker__empty">Loading labels…</div> : null}
          {error ? <div className="label-picker__error">Could not load labels: {error.message}</div> : null}
          {data && !options.length ? <div className="label-picker__empty">No label matches “{q}”.</div> : null}
          {err ? (
            <div className="label-picker__error" role="alert">
              {err}
            </div>
          ) : null}
          <div className="label-picker__foot">A scoped label (scope::value) replaces any other label of its scope.</div>
        </div>
      ) : null}
    </div>
  );
}
